// Berechnungen der Übersicht (Spezifikation 4.1.4) als reine Funktionen: keine Seiteneffekte, kein Vue, keine Requests.
// Monat M / Jahr Y kommen aus dem Monatsschritt ('YYYY-MM'). Alle Eingaben sind die unveränderten Antworten der
// bestehenden Endpunkte (ticketsale option=overview, saldo, driversettlement, gps, user, device); Werte werden
// defensiv gelesen (Zahl oder Text), fehlende oder ungültige Werte zählen als 0.
import moment from 'moment';
import { parseApiDecimal } from '@/utils/format';

// mehr als 10 Minuten (gps.last_delay in Sekunden) gilt ein Bus als verspätet (Kundenwunsch 12.10.2026, vorher ab 5)
export const LATE_SECONDS = 600;
// bis 5 Minuten gilt ein Bus als pünktlich (grün), darüber bis 10 Minuten gelb
export const WARN_SECONDS = 300;
// Legende der Ampel: gleiche Stufen wie delayLevel, gemeinsam für Dashboard (Kachel, Live-Karte) und Live-Tracking
export const DELAY_LEGEND = Object.freeze([
  Object.freeze({ key: 'ok', dot: 'var(--st-ok)', text: '≤ 5 Min.' }),
  Object.freeze({ key: 'warn', dot: 'var(--st-warn)', text: '5–10 Min.' }),
  Object.freeze({ key: 'late', dot: 'var(--st-late)', text: '> 10 Min.' })
]);

function num(v) {
  const n = Number(v);
  return isFinite(n) ? n : 0;
}

function list(rows) {
  return Array.isArray(rows) ? rows : [];
}

// auf Cent runden (Summen aus Gleitkommawerten)
function cents(x) {
  return Math.round(x * 100) / 100;
}

// 'YYYY-MM' → { year, month } (month 1–12); sonst null
export function splitMonth(value) {
  const m = /^(\d{4})-(\d{2})$/.exec(value || '');
  if (!m) {
    return null;
  }
  const month = Number(m[2]);
  return month >= 1 && month <= 12 ? { year: Number(m[1]), month } : null;
}

export function daysInMonth(year, month) {
  return moment({ year, month: month - 1, day: 1 }).daysInMonth();
}

// Heute als { key, utcKey, year, month, day }; key = lokales Datum 'YYYY-MM-DD', utcKey = UTC-Datum (Zeilen „von heute“
// in der Saldo-Antwort, Spezifikation: date.slice(0, 10) === moment.utc().format('YYYY-MM-DD')).
export function todayInfo(key, utcKey) {
  const m = moment(key, 'YYYY-MM-DD');
  return { key, utcKey, year: m.year(), month: m.month() + 1, day: m.date() };
}

// ---------------------------------------------------------------- ticketsale?option=overview
// Wert der ersten Zeile; leeres Array → 0. sum kommt als en-US-Text von MySQL FORMAT() („13,091.69“).
export function overviewValues(rows) {
  const row = list(rows)[0];
  if (!row) {
    return { sum: 0, count: 0 };
  }
  return { sum: parseApiDecimal(row.sum), count: parseApiDecimal(row.count) };
}

// ---------------------------------------------------------------- saldo
function inMonths(row, year, month) {
  const m = num(row.billing_month);
  return num(row.billing_year) === year && m >= 1 && m <= month;
}

// Ø je Tag für die Abrechnungsmonate 1..M des Jahres Y: Σ field ÷ Tage des Monats. Im laufenden Monat ohne die Zeilen
// von heute und geteilt durch (Tag − 1); vor dem ersten vollen Tag ist der Wert null.
// field: 'sale_amount' (Einnahmen) oder 'sale_count' (Ticketverkäufe).
export function monthlyAverages(rows, year, month, field, today) {
  const sums = [];
  for (let i = 0; i < month; i += 1) {
    sums.push(0);
  }
  list(rows).forEach((row) => {
    if (!row || !inMonths(row, year, month)) {
      return;
    }
    const m = num(row.billing_month);
    const current = year === today.year && m === today.month;
    if (current && typeof row.date === 'string' && row.date.slice(0, 10) === today.utcKey) {
      return;
    }
    sums[m - 1] += num(row[field]);
  });
  return sums.map((sum, i) => {
    const m = i + 1;
    const days = year === today.year && m === today.month ? today.day - 1 : daysInMonth(year, m);
    return days > 0 ? sum / days : null;
  });
}

// Veränderung Ø M gegenüber Ø M−1 als Bruch (0.042 = +4,2 %); nur innerhalb des Jahres und wenn Ø M−1 > 0, sonst null
export function averageTrend(averages, month) {
  if (month < 2) {
    return null;
  }
  const current = averages[month - 1];
  const previous = averages[month - 2];
  if (current === null || current === undefined || !(previous > 0)) {
    return null;
  }
  return current / previous - 1;
}

// Saldo {Y}: Σ diff_amount über billing_year = Y und billing_month ≤ M; Minibalken je Monat max(0, −Σ);
// Salden je Fahrer (user_id) auf Cent gerundet, offen = < 0 (aufsteigend, der größte offene Betrag zuerst).
export function saldoSummary(rows, year, month) {
  let total = 0;
  const perMonth = [];
  for (let i = 0; i < month; i += 1) {
    perMonth.push(0);
  }
  const perUser = {};
  list(rows).forEach((row) => {
    if (!row || !inMonths(row, year, month)) {
      return;
    }
    const diff = num(row.diff_amount);
    total += diff;
    perMonth[num(row.billing_month) - 1] += diff;
    const id = String(row.user_id);
    perUser[id] = (perUser[id] || 0) + diff;
  });
  const users = Object.keys(perUser).map(id => ({ id, cents: Math.round(perUser[id] * 100) }));
  const open = users
    .filter(u => u.cents < 0)
    .sort((a, b) => a.cents - b.cents)
    .map(u => ({ id: u.id, amount: u.cents / 100 }));
  return {
    total: cents(total),
    bars: perMonth.map(v => Math.max(0, -cents(v))),
    open,
    openSum: cents(open.reduce((acc, u) => acc + u.amount, 0)),
    balancedCount: users.length - open.length
  };
}

// Zahlungsarten im Abrechnungsmonat M/Y: Bar = Σ sale_amount_cash, Karte = Σ sale_amount_sumup, Gutschein =
// Σ sale_amount_voucher (Backend seit Oktober 2026; Bar dann ohne Gutscheine, ältere Antworten ohne das Feld = 0),
// Anteil = Teil ÷ (Bar + Karte + Gutschein); ohne Verkäufe empty = true.
export function paymentSplit(rows, year, month) {
  let cash = 0;
  let card = 0;
  let voucher = 0;
  list(rows).forEach((row) => {
    if (!row || num(row.billing_year) !== year || num(row.billing_month) !== month) {
      return;
    }
    cash += num(row.sale_amount_cash);
    card += num(row.sale_amount_sumup);
    voucher += num(row.sale_amount_voucher);
  });
  cash = cents(cash);
  card = cents(card);
  voucher = cents(voucher);
  const total = cents(cash + card + voucher);
  return {
    cash,
    card,
    voucher,
    total,
    empty: cash === 0 && card === 0 && voucher === 0,
    cashShare: total > 0 ? cash / total : null,
    cardShare: total > 0 ? card / total : null,
    voucherShare: total > 0 ? voucher / total : null
  };
}

// Offene Beträge: höchstens drei Fahrer mit Namen aus der user-Liste (fullname, sonst „Fahrer {id}“), Balkenbreite
// |v| ÷ max|v|; Rest (mehr als drei offen) mit Anzahl und Summe; ausgeglichen = alle übrigen Fahrer (auch Guthaben).
export function openAmounts(summary, users, limit = 3) {
  const names = {};
  list(users).forEach((user) => {
    if (user && user.user_id !== undefined && user.user_id !== null && user.fullname) {
      names[String(user.user_id)] = user.fullname;
    }
  });
  const all = summary ? summary.open : [];
  const shown = all.slice(0, limit);
  const rest = all.slice(limit);
  const max = shown.reduce((acc, u) => Math.max(acc, Math.abs(u.amount)), 0) || 1;
  return {
    rows: shown.map(u => ({
      id: u.id,
      name: names[u.id] || 'Fahrer ' + u.id,
      amount: u.amount,
      share: Math.abs(u.amount) / max
    })),
    restCount: rest.length,
    restSum: cents(rest.reduce((acc, u) => acc + u.amount, 0)),
    openCount: all.length,
    openSum: summary ? summary.openSum : 0,
    balancedCount: summary ? summary.balancedCount : 0
  };
}

// Laufender Monat für Saldo-Abfragen: im laufenden Jahr der laufende Monat (wie bisher), für Vorjahre 12
export function saldoToMonth(year, today) {
  return year === today.year ? today.month : 12;
}

// ---------------------------------------------------------------- driversettlement
// Zeitraum der Karte „Einnahmen“: laufender Monat → heute−29 bis heute; vergangener Monat → 1. bis Monatsende
export function revenueRange(year, month, today) {
  if (year === today.year && month === today.month) {
    const to = moment(today.key, 'YYYY-MM-DD');
    return { from: to.clone().subtract(29, 'days').format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD'), current: true };
  }
  const first = moment({ year, month: month - 1, day: 1 });
  return { from: first.format('YYYY-MM-DD'), to: first.clone().endOf('month').format('YYYY-MM-DD'), current: false };
}

// Σ total je Tag über alle Fahrer (inkl. Testfahrer, passend zur Kennzahl), fehlende Tage 0
export function revenueDays(drivers, from, to) {
  const byDate = {};
  list(drivers).forEach((driver) => {
    list(driver && driver.days).forEach((day) => {
      if (!day || !day.date) {
        return;
      }
      const key = String(day.date).slice(0, 10);
      byDate[key] = (byDate[key] || 0) + num(day.total);
    });
  });
  const days = [];
  const end = moment(to, 'YYYY-MM-DD');
  for (const d = moment(from, 'YYYY-MM-DD'); !d.isAfter(end, 'day'); d.add(1, 'day')) {
    const key = d.format('YYYY-MM-DD');
    days.push(Object.freeze({ date: key, value: cents(byDate[key] || 0) }));
  }
  return Object.freeze(days);
}

// Achse des Säulendiagramms: Schritt = kleinste Zahl aus {1; 2; 2,5; 5} · 10ⁿ, die ≥ max/3 ist (max 0 → 1000)
export function axisStep(max) {
  if (!(max > 0)) {
    return 1000;
  }
  const target = max / 3;
  const exp = Math.floor(Math.log10(target));
  const factors = [1, 2, 2.5, 5];
  for (let e = exp - 1; e <= exp + 1; e += 1) {
    for (let i = 0; i < factors.length; i += 1) {
      const step = factors[i] * Math.pow(10, e);
      if (step >= target * (1 - 1e-9)) {
        return step;
      }
    }
  }
  return Math.pow(10, exp + 1);
}

// ---------------------------------------------------------------- gps, user, device
export function isLate(row) {
  return !!row && Number(row.last_delay) > LATE_SECONDS;
}

// Ampelstufe eines Busses (Kachel, Karte, Legende; Kundenwunsch 12.10.2026): 'ok' bis 5 Min. (auch zu früh), 'warn'
// über 5 bis 10 Min., 'late' über 10 Min. (isLate), 'none' ohne Wert
export function delayLevel(row) {
  const raw = row ? row.last_delay : null;
  if (raw === null || raw === undefined || raw === '' || !isFinite(Number(raw))) {
    return 'none';
  }
  if (isLate(row)) {
    return 'late';
  }
  return Number(raw) > WARN_SECONDS ? 'warn' : 'ok';
}

// Busse unterwegs: n Zeilen, davon verspätet (> 10 Min.), Zahl je Ampelstufe, Anzahl verschiedener Linien (ohne leere
// line_id)
export function busSummary(rows) {
  const buses = list(rows);
  const lines = {};
  const levels = { ok: 0, warn: 0, late: 0, none: 0 };
  buses.forEach((row) => {
    if (row && row.line_id !== null && row.line_id !== undefined && row.line_id !== '') {
      lines[String(row.line_id)] = true;
    }
    levels[delayLevel(row)] += 1;
  });
  return { n: buses.length, late: levels.late, levels, lines: Object.keys(lines).length };
}

// Aktive Benutzer bzw. Geräte: aktiv = Number(active) === 1, gesamt = Listenlänge (Test-Einträge zählen mit)
export function activeSummary(rows) {
  const items = list(rows);
  const active = items.filter(row => row && Number(row.active) === 1).length;
  return { active, total: items.length, inactive: items.length - active };
}
